import React from 'react'
import PropTypes from 'prop-types'
import { StyledFormField, StyledSelect } from './FormSelect.styled.js'
import { FormFieldError } from '../FormFieldError/FormFieldError.jsx'

/**
 * FormSelect View Component
 * Pure presentation component for select/dropdown fields using react-select
 */
export const FormSelectView = ({
  label,
  placeholder,
  required,
  disabled,
  loading,
  search,
  clearable,
  multiple,
  error,
  errorMessage,
  reactSelectOptions,
  reactSelectValue,
  onChange,
  onBlur,
  onInputChange,
  inputValue,
  filterOption,
  noOptionsMessage,
  name,
  selectRef,
  'data-testid': dataTestId,
  'data-automation-id': dataAutomationId,
}) => {
  // Render an option's label with an optional leading icon node, an optional
  // class (Semantic's `muted` and friends) and an optional secondary
  // description line. react-select calls this for both the menu rows and the
  // selected value, so `context` decides what belongs where: descriptions are
  // menu-only, exactly as Semantic's Dropdown rendered them. An option carrying
  // none of the three returns the plain label string, so every existing select
  // stays visually unchanged.
  const renderOptionLabel = (option, meta) => {
    const label =
      option.icon || option.className ? (
        <span
          className={option.className}
          style={
            option.icon
              ? { display: 'inline-flex', alignItems: 'center', gap: '0.4em' }
              : undefined
          }
        >
          {option.icon}
          {option.label}
        </span>
      ) : (
        option.label
      )

    if (!option.description || meta?.context !== 'menu') {
      return label
    }

    return (
      <span>
        {label}
        <span className='react-select__option-description'>
          {option.description}
        </span>
      </span>
    )
  }

  // Custom ref callback to properly forward react-select focus to React Hook Form
  // react-select's ref exposes the Select instance which has a .focus() method
  const handleRef = (selectInstance) => {
    if (selectInstance && selectRef) {
      // Call the RHF ref function with the react-select instance
      // react-select exposes .focus() method on its instance
      if (typeof selectRef === 'function') {
        selectRef(selectInstance)
      } else {
        selectRef.current = selectInstance
      }
    }
  }

  return (
    <StyledFormField
      $error={error}
      $required={required}
      $disabled={disabled}
      data-testid={dataTestId}
      data-automation-id={dataAutomationId}
    >
      <FormFieldError error={errorMessage} show={error} />
      {label && <label>{label}</label>}
      <StyledSelect
        ref={handleRef}
        name={name}
        value={reactSelectValue}
        onChange={onChange}
        onBlur={onBlur}
        onInputChange={onInputChange}
        inputValue={inputValue}
        options={reactSelectOptions}
        formatOptionLabel={renderOptionLabel}
        placeholder={placeholder || 'Select...'}
        isDisabled={disabled}
        isLoading={loading}
        isSearchable={search}
        isClearable={clearable}
        isMulti={multiple}
        filterOption={filterOption}
        noOptionsMessage={noOptionsMessage}
        classNamePrefix='react-select'
        $error={error}
        $disabled={disabled}
      />
    </StyledFormField>
  )
}

FormSelectView.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  search: PropTypes.bool,
  clearable: PropTypes.bool,
  multiple: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  reactSelectOptions: PropTypes.arrayOf(
    PropTypes.shape({
      // See FormSelect.propTypes: a null-valued sentinel option is valid
      value: PropTypes.any,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      description: PropTypes.string,
      className: PropTypes.string,
    }),
  ).isRequired,
  reactSelectValue: PropTypes.oneOfType([
    PropTypes.shape({
      value: PropTypes.any,
      label: PropTypes.string,
      icon: PropTypes.node,
      description: PropTypes.string,
      className: PropTypes.string,
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.any,
        label: PropTypes.string,
        icon: PropTypes.node,
        description: PropTypes.string,
        className: PropTypes.string,
      }),
    ),
  ]),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  onInputChange: PropTypes.func,
  inputValue: PropTypes.string,
  filterOption: PropTypes.func,
  noOptionsMessage: PropTypes.func,
  name: PropTypes.string.isRequired,
  selectRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  'data-testid': PropTypes.string,
  'data-automation-id': PropTypes.string,
}

FormSelectView.defaultProps = {
  disabled: false,
  required: false,
  loading: false,
  search: false,
  clearable: false,
  multiple: false,
}
